import { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { Channel, Program } from "@hybrid-iptv/types";
import { EpgEmptyBlock, EpgProgramBlock } from "./EpgProgramBlock.js";
import { EpgTimeline, PIXELS_PER_HOUR } from "./EpgTimeline.js";
import { CHANNEL_ROW_HEIGHT } from "./ChannelRow.js";
import { epgWindowStart, GUIDE_FIRST_HOURS, requestGuideFar } from "./hooks.js";
import type { ChannelProgramIndex } from "./hooks.js";

interface EpgGridProps {
  channels: Channel[];
  programIndex: ChannelProgramIndex | null;
  /** How far the grid reaches (GUIDE_REACH_HOURS). */
  timeWindowHours: number;
  /** Hours across the visible width (the 2 h / 4 h setting); sets the hour width. */
  hoursOnScreen?: number;
  selectedChannelId: string | null;
  sharedScrollRef: React.RefObject<HTMLDivElement | null>;
  onProgramSelect: (program: Program, channel: Channel) => void;
  /** Long-press / hold-OK / right-click on a programme — opens the quick-action
   * menu at the given viewport point. */
  onProgramContext?: (program: Program, channel: Channel, point: { x: number; y: number }) => void;
  /** OK on a row's "No information" block: the same as OK on the channel's name. */
  onChannelSelect?: (channel: Channel) => void;
  /** Long-press on a row's "No information" block: the channel's quick-action menu. */
  onChannelContext?: (channel: Channel, point: { x: number; y: number }) => void;
  /** Current D-pad focus position (for the roving ring + tabindex). */
  focusRow?: number;
  focusCol?: number;
  /** Called when a block is clicked, so the D-pad ring follows the mouse. */
  onFocusAt?: (row: number, col: number) => void;
}

const OVERSCAN = 8;

/**
 * The grid's visible width, remembered between opens. Measured fresh, the grid
 * drew every row at a guessed hour width and then again at the real one —
 * part of the 2 s the guide lost opening on the onn box (2026-10-05). With the
 * width remembered it draws once; only a first open, or a changed screen, redraws.
 */
const WIDTH_KEY = "epg-grid-width";
function rememberedWidth(): number {
  try {
    const value = Number(localStorage.getItem(WIDTH_KEY));
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

/**
 * Virtualized EPG grid. The left-hand scroll rail owns vertical scrolling;
 * this pane owns only horizontal timeline scrolling. This prevents the
 * channel list and programme rows from ever acquiring different scrollTop
 * values while preserving normal horizontal guide navigation.
 */
export function EpgGrid({
  channels,
  programIndex,
  timeWindowHours,
  hoursOnScreen = 4,
  selectedChannelId,
  sharedScrollRef,
  onProgramSelect,
  onProgramContext,
  onChannelSelect,
  onChannelContext,
  focusRow = 0,
  focusCol = -1,
  onFocusAt,
}: EpgGridProps) {
  const [now, setNow] = useState(() => new Date());
  const horizontalScrollRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  const windowStart = useMemo(() => epgWindowStart(now), [now]);
  const windowEnd = useMemo(
    () => new Date(windowStart.getTime() + timeWindowHours * 3600_000),
    [windowStart, timeWindowHours],
  );
  // One hour is the grid's visible width over the hours chosen for the screen
  // (Settings → Guide, 2 or 4), so 2 hours fill it whatever the screen; the
  // grid itself runs on for timeWindowHours.
  const [viewWidth, setViewWidth] = useState(rememberedWidth);
  useEffect(() => {
    const el = horizontalScrollRef.current;
    if (!el) return;
    const measure = () => {
      const width = el.clientWidth;
      if (width <= 0) return;
      setViewWidth((current) => (Math.abs(current - width) > 1 ? width : current));
      try {
        localStorage.setItem(WIDTH_KEY, String(width));
      } catch {
        // Private mode: measured each time instead.
      }
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const pxPerHour = viewWidth > 0 ? viewWidth / hoursOnScreen : PIXELS_PER_HOUR;
  const totalWidth = timeWindowHours * pxPerHour;
  const totalHeight = channels.length * CHANNEL_ROW_HEIGHT;

  const rowVirtualizer = useVirtualizer({
    count: channels.length,
    getScrollElement: () => sharedScrollRef.current,
    estimateSize: () => CHANNEL_ROW_HEIGHT,
    overscan: OVERSCAN,
  });

  useEffect(() => {
    if (!selectedChannelId) return;
    const idx = channels.findIndex((channel) => channel.id === selectedChannelId);
    if (idx >= 0) rowVirtualizer.scrollToIndex(idx, { align: "center" });
  }, [selectedChannelId, channels, rowVirtualizer]);

  const syncHorizontalScroll = useCallback((source: HTMLDivElement | null, target: HTMLDivElement | null) => {
    if (!source || !target || target.scrollLeft === source.scrollLeft) return;
    target.scrollLeft = source.scrollLeft;
  }, []);

  const scrollOffset = rowVirtualizer.scrollOffset ?? 0;
  const nowLeftPx = useMemo(() => {
    const elapsed = (now.getTime() - windowStart.getTime()) / 1000;
    return (elapsed / 3600) * pxPerHour;
  }, [now, windowStart, pxPerHour]);

  return (
    <div className="epg-grid-wrapper">
      <div
        className="epg-timeline-header"
        ref={timelineRef}
        onScroll={() => syncHorizontalScroll(timelineRef.current, horizontalScrollRef.current)}
      >
        <div style={{ width: totalWidth, minWidth: "100%" }}>
          <EpgTimeline windowStart={windowStart} hours={timeWindowHours} pxPerHour={pxPerHour} />
        </div>
      </div>
      <div
        className="epg-grid-rows"
        ref={horizontalScrollRef}
        onScroll={() => {
          syncHorizontalScroll(horizontalScrollRef.current, timelineRef.current);
          // Nearing the end of the first hours loaded: load the rest of the reach.
          const el = horizontalScrollRef.current;
          if (el && el.scrollLeft + el.clientWidth > (GUIDE_FIRST_HOURS - 1) * pxPerHour) requestGuideFar();
        }}
      >
        <div
          className="epg-grid-content"
          style={{ width: totalWidth, height: totalHeight, transform: `translateY(${-scrollOffset}px)` }}
        >
          {nowLeftPx >= 0 && nowLeftPx <= totalWidth && (
            <div className="epg-now-line" style={{ left: nowLeftPx, height: totalHeight }} />
          )}
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const channel = channels[virtualRow.index]!;
            const programs = programIndex?.getProgramsInWindow(channel.id, windowStart, windowEnd) ?? [];
            return (
              <div
                key={virtualRow.key}
                className="epg-grid-row"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: virtualRow.size,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {programIndex && programs.length === 0 && (
                  <EpgEmptyBlock
                    widthPx={totalWidth}
                    guideRow={virtualRow.index}
                    isFocused={focusRow === virtualRow.index && focusCol === 0}
                    onClick={() => {
                      onFocusAt?.(virtualRow.index, 0);
                      onChannelSelect?.(channel);
                    }}
                    onContextAction={
                      onChannelContext
                        ? (point) => {
                            onFocusAt?.(virtualRow.index, 0);
                            onChannelContext(channel, point);
                          }
                        : undefined
                    }
                  />
                )}
                {programs.map((program, programCol) => {
                  const startMs = new Date(program.start).getTime();
                  const endMs = new Date(program.end).getTime();
                  // A programme already running when the window starts is drawn
                  // from the window's start: placed at its real start, its time
                  // and title sat off the left edge and the focused block showed
                  // blank (projector, 2026-09-26).
                  const realLeft = ((startMs - windowStart.getTime()) / 3600_000) * pxPerHour;
                  const left = Math.max(0, realLeft);
                  const width = ((endMs - startMs) / 3600_000) * pxPerHour - (left - realLeft);
                  const isLive = now >= new Date(program.start) && now < new Date(program.end);
                  return (
                    <EpgProgramBlock
                      key={program.id}
                      program={program}
                      leftPx={left}
                      widthPx={width}
                      isLive={isLive}
                      guideRow={virtualRow.index}
                      guideCol={programCol}
                      isFocused={focusRow === virtualRow.index && focusCol === programCol}
                      onClick={() => {
                        onFocusAt?.(virtualRow.index, programCol);
                        onProgramSelect(program, channel);
                      }}
                      onContextAction={
                        onProgramContext
                          ? (point) => {
                              onFocusAt?.(virtualRow.index, programCol);
                              onProgramContext(program, channel, point);
                            }
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
